/**
 * Componente: TerminalSelector
 * Permite cambiar entre terminales disponibles para el usuario
 */

import React, { useEffect, useState } from "react";
import {
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
  DropdownToggle,
} from "reactstrap";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import Swal from 'sweetalert2';

const TerminalSelector = ({ className = "", insideDropdown = false }) => {
  const { user, updateUserTerminal } = useAuth();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Crear estilos para el dropdown
    const style = document.createElement('style');
    style.textContent = `
      .terminal-selector-dropdown {
        min-width: 250px;
      }

      .terminal-selector-item {
        cursor: pointer;
        padding: 10px 15px;
        border-radius: 4px;
        margin: 2px 5px;
        transition: all 0.2s ease;
      }

      .terminal-selector-item:hover {
        background: rgba(220, 1, 127, 0.1);
        border-left: 3px solid #DC017F;
      }

      .terminal-selector-item.active {
        background: linear-gradient(135deg, rgba(90, 12, 98, 0.2) 0%, rgba(220, 1, 127, 0.2) 100%);
        border-left: 4px solid #DC017F;
        font-weight: 600;
      }

      .terminal-selector-toggle {
        width: 100%;
        min-width: 280px;

        background: linear-gradient(
          135deg,
          #5A0C62 0%,
          #DC017F 100%
        );

        border: none;
        color: white;

        height: 42px;
        padding: 0 14px;

        display: flex;
        align-items: center;
        justify-content: space-between;

        border-radius: 8px;

        font-size: 14px;
        font-weight: 600;
        box-shadow: 0 2px 6px rgba(0,0,0,.15);
      }

      .terminal-selector-toggle:hover,
      .terminal-selector-toggle:focus,
      .terminal-selector-toggle:active {
        background: linear-gradient(135deg, #6B1473 0%, #E6018F 100%);
        box-shadow: 0 4px 12px rgba(90, 12, 98, 0.3);
        transform: translateY(-1px);
      }

      .terminal-selector-label {
        display: block;
        font-size: 10px;
        text-transform: uppercase;
        font-weight: 700;
        letter-spacing: 1px;
        opacity: 0.8;
        margin-bottom: 2px;
      }

      .terminal-selector-current {
        display: block;
        font-size: 13px;
        font-weight: 700;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 200px;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const handleTerminalChange = async (terminalId, terminalName) => {
    if (terminalId === user.terminal_id) {
      return; // No hacer nada si es la terminal actual
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/cambiar-terminal`,
        { terminal_id: terminalId },
        {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        // Actualizar el contexto con la nueva terminal
        updateUserTerminal(
          response.data.terminal_id, 
          response.data.terminal_name,
          response.data.terminal_logo,
          response.data.terminal_descripcion
        );

        // Actualizar localStorage inmediatamente para que el refresh lo tome al instante
        if (response.data.terminal_logo) {
          localStorage.setItem("terminal_logo", response.data.terminal_logo);
        } else {
          localStorage.removeItem("terminal_logo");
        }

        if (response.data.terminal_descripcion) {
          localStorage.setItem("terminal_descripcion", response.data.terminal_descripcion);
        } else {
          localStorage.removeItem("terminal_descripcion");
        }

        await Swal.fire({
          icon: 'success',
          title: '¡Terminal Cambiada!',
          text: `Ahora está en: ${terminalName}`,
          toast: true,
          position: 'top-right',
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true,
        });

        // Recargar la página para actualizar los datos
        window.location.reload();
      } else {
        throw new Error(response.data.error || 'Error al cambiar terminal');
      }
    } catch (error) {
      console.error('Error cambiando terminal:', error);

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.error || error.message || 'Error al cambiar de terminal',
        confirmButtonColor: 'linear-gradient(135deg, rgb(90, 12, 98) 0%, rgb(220, 1, 127) 100%)',
      });
    } finally {
      setLoading(false);
    }
  };

  // No mostrar si el usuario solo tiene una terminal
  if (!user?.available_terminals || user.available_terminals.length <= 1) {
    return null;
  }

  return (
    <UncontrolledDropdown className={`terminal-selector ${className}`} nav={!insideDropdown}>
      <DropdownToggle
        className="terminal-selector-toggle"
        caret
        disabled={loading}
      >
        <div className="d-flex align-items-center">
          <i className="fas fa-location-dot mr-2"></i>

          <span>
            {loading
              ? "Cargando..."
              : user?.terminal_name}
          </span>
        </div>
      </DropdownToggle>

      <DropdownMenu
        right
        className="terminal-selector-dropdown"
      >
        <div style={{ padding: '10px 0' }}>
          {user?.available_terminals?.map((terminal) => (
            <DropdownItem
              key={terminal.id}
              onClick={() => handleTerminalChange(terminal.id, terminal.nombre)}
              disabled={loading}
              className={`terminal-selector-item ${
                terminal.id === user?.terminal_id ? 'active' : ''
              }`}
              style={{
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {terminal.id === user?.terminal_id && (
                  <i className="fas fa-check-circle" style={{ color: '#DC017F' }} />
                )}
                <span>{terminal.nombre}</span>
              </div>
            </DropdownItem>
          ))}
        </div>
      </DropdownMenu>
    </UncontrolledDropdown>
  );
};

export default TerminalSelector;
